'use client';

import {
  DeliveryType,
  type Path,
  type Service,
  type ServiceWithEstimate,
} from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore } from '@ahm/react-map/store';
import { useMediaQuery } from '@mantine/hooks';
import { useMutationState } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';

import { useGetServiceTypes } from '../../api/service';
import { HmServiceList } from './hm-service-list';
import { getMaxCod, showAlertNoService } from './service-shared-utils';
import { ROUTES } from '@/config/router';
import { useHmEstimatePackagesMutation } from '@/features/booking/api/service/mutation-hm-estimtate-packages';
import { useGetLocationFeatures } from '@/features/booking/api/service/use-get-location-features';
import { useRemoteConfigHook } from '@/hooks';
import { ActionIndexPath } from '@/lib/constants';
import { getServiceType } from '@/lib/utils';
import { useLocaleSuffix, usePathname } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type UseHmServiceReturn = {
  isLoading: boolean;
  isDisabled: boolean;
  isHiddenHm: boolean;
  estimatedServices: ServiceWithEstimate[];
  hmEstimatedServicesGroup: Record<string, ServiceWithEstimate[]> | null;
  pickupPath: Path | null;
  onOpenPopupAllService: () => void;
  onClosePopupAllService: () => void;
  renderHmEstimatedServices: () => JSX.Element | JSX.Element[] | null;
};

// ============================================================================
// Helper Functions
// ============================================================================

const mapServicesWithEstimate = (
  services: Service[],
  estimates: any[]
): ServiceWithEstimate[] => {
  if (!services?.length) return [];
  if (!estimates?.length) return services;

  const estimateMap = new Map(estimates.map((est) => [est.service_id, est]));

  return services.map((service) => ({
    ...service,
    ...(estimateMap.has(service._id) && {
      estimate_package: estimateMap.get(service._id),
    }),
  }));
};

const groupServicesByPackage = (
  services: ServiceWithEstimate[]
): Record<string, ServiceWithEstimate[]> | null => {
  if (!services.length) return null;

  const grouped: Record<string, ServiceWithEstimate[]> = {};
  const others: ServiceWithEstimate[] = [];

  for (const service of services) {
    const groupName = (service as any)?.estimate_package?.group_name;

    if (groupName) {
      if (!grouped[groupName]) grouped[groupName] = [];
      grouped[groupName].push(service);
    } else {
      others.push(service);
    }
  }

  if (others.length) grouped.Others = others;

  return grouped;
};

function getDeliveryType(pathname: string): DeliveryType | undefined {
  const deliveryTypeMap: Record<string, DeliveryType> = {
    [ROUTES.nationwide]: DeliveryType.Nationwide,
    [ROUTES.truck]: DeliveryType.Truck,
    [ROUTES.rental]: DeliveryType.Rental,
  };
  return deliveryTypeMap[pathname];
}

// ============================================================================
// Main Hook
// ============================================================================

export function useHmService(): UseHmServiceReturn {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const localeSuffix = useLocaleSuffix();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const loadServiceParam = searchParams.get('load_service');

  const [activeItem, setActiveItem] = useState('');
  // const prevPathsRef = useRef<string>('');

  const { session, webInAppConfig } = useAuth();
  const user = session?.user;
  const { surgePriceConfig } = useRemoteConfigHook();

  const {
    serviceTypes,
    estimatedAllService,
    selectedService,
    setSelectedService,
    setServiceTypes,
    setIsEstimateAllService,
    setOpenPopupAllService,
    isOpenPopupAllService,
    setAlertBooking,
    setIsSuggestPromotion,
  } = useBookingStore((state) => state);

  const {
    paths,
    loading: loadingLocation,
    setPaths,
  } = useLocationStore((state) => state);
  const { mutateAsync: hmEstimatePackagesMutation } =
    useHmEstimatePackagesMutation();

  // ============================================================================
  // Computed Values
  // ============================================================================

  const isHMService = useMemo(
    () => getServiceType(pathname).isHMService,
    [pathname]
  );

  const { data: locationFeatures } = useGetLocationFeatures({
    enabled: isHMService,
  });

  const pickupPath = useMemo(
    () => paths[ActionIndexPath.Pickup] ?? null,
    [paths]
  );

  const deliveryType = useMemo(() => getDeliveryType(pathname), [pathname]);

  const pathsKey = useMemo(
    () => paths.map((p) => `${p?.lat},${p?.lng}`).join('|'),
    [paths]
  );

  const pathsLength = paths.length;
  const shouldFetchServices = Boolean(
    user && pickupPath?.lat && pickupPath?.lng
  );

  // ============================================================================
  // Pending Mutations
  // ============================================================================

  const isEstimatingIntercityOrder = useMutationState({
    filters: { status: 'pending', mutationKey: ['estimateIntercityOrderFn'] },
    select: (mutation) => mutation.state.status,
  });

  const isEstimatingNewOrder = useMutationState({
    filters: { status: 'pending', mutationKey: ['estimateNewOrderFn'] },
    select: (mutation) => mutation.state.status,
  });

  // ============================================================================
  // API Query
  // ============================================================================

  const { data: serviceTypesData, isLoading: isLoadingServiceTypes } =
    useGetServiceTypes(
      {
        lat: Number(pickupPath?.lat),
        lng: Number(pickupPath?.lng),
        delivery_type: deliveryType,
        user_mobile: loadServiceParam && user?._id ? user._id : undefined,
      },
      {
        enabled: shouldFetchServices,
        staleTime: 10_000,
      }
    );

  // ============================================================================
  // Memoized Data Processing
  // ============================================================================

  const estimatedServices = useMemo((): ServiceWithEstimate[] => {
    if (!serviceTypes?.length) return [];
    if (!estimatedAllService?.length) return serviceTypes;

    const estimateMap = new Map(
      estimatedAllService.map((est: any) => [est.service_id, est])
    );

    return serviceTypes.map((service) => {
      const estimate = estimateMap.get(service._id);
      return estimate ? { ...service, estimate_data: estimate } : service;
    });
  }, [serviceTypes, estimatedAllService]);

  const hmEstimatedServicesGroup = useMemo(
    () => groupServicesByPackage(estimatedServices),
    [estimatedServices]
  );

  // ============================================================================
  // Service Processing Effect
  // ============================================================================

  useEffect(() => {
    if (
      isLoadingServiceTypes ||
      !serviceTypesData
      // ||
      // pathsKey === prevPathsRef.current
    ) {
      return;
    }

    // prevPathsRef.current = pathsKey;

    const webInAppId = webInAppConfig?.id;
    const filteredServices = serviceTypesData.filter((s) => {
      const enablePlatform = s.enable_platform ?? [];
      return (
        !webInAppId ||
        !enablePlatform.length ||
        enablePlatform.includes(webInAppId)
      );
    });

    const processedServices = filteredServices.map((s) => {
      const { max_cod, max_cod_per_stop_point } = getMaxCod(
        s,
        user,
        webInAppConfig
      );
      return {
        ...s,
        max_cod,
        max_cod_per_stop_point,
        payment_method:
          webInAppConfig?.paymentMethods?.map((method) => ({
            payment: method,
          })) ?? [],
      };
    });

    if (!processedServices.length) {
      showAlertNoService(setAlertBooking, pathsLength);
      return;
    }

    const shouldEstimateHM = isHMService && pathsLength === 2;

    if (shouldEstimateHM) {
      hmEstimatePackagesMutation({
        idle_util: 0,
        path: paths.map((path) => ({
          lat: path.lat,
          lng: path.lng,
          location_features: (path as any).location_features,
        })),
      })
        .then((estimates) => {
          const mapped = mapServicesWithEstimate(
            processedServices,
            estimates ?? []
          );
          setServiceTypes(mapped, pathname);
        })
        .catch((error) => {
          console.error('HM estimate failed:', error);
          setServiceTypes(processedServices, pathname);
        });
    } else {
      setServiceTypes(processedServices, pathname);
    }
  }, [
    serviceTypesData,
    isLoadingServiceTypes,
    pathsKey,
    pathsLength,
    webInAppConfig,
    user,
    setAlertBooking,
    isHMService,
    paths,
    hmEstimatePackagesMutation,
    setServiceTypes,
    pathname,
  ]);

  useEffect(() => {
    if (pathsLength === 2 && locationFeatures?.length > 0) {
      let countDif = 0;
      const newPaths = paths.map((p) => {
        if (p?.location_features?.length > 0) return p;
        else {
          countDif++;
          return {
            ...p,
            location_features: [locationFeatures[0].code],
          };
        }
      });
      if (countDif > 0) {
        setPaths(newPaths);
      }
    }
  }, [locationFeatures, setPaths]);

  // ============================================================================
  // Selection Handlers
  // ============================================================================

  const selectServiceById = useCallback(
    (serviceId: string) => {
      const service = estimatedServices.find((s) => s._id === serviceId);
      if (!service) return;

      setIsSuggestPromotion(true);
      setSelectedService(service);
      setOpenPopupAllService(false);
      setIsEstimateAllService(false);
    },
    [
      estimatedServices,
      setIsSuggestPromotion,
      setSelectedService,
      setOpenPopupAllService,
      setIsEstimateAllService,
    ]
  );

  const handleSelectVehicle = useCallback(
    (serviceId: string) => {
      if (!serviceId) return;

      if (isMobile) {
        setActiveItem(serviceId);
      } else {
        selectServiceById(serviceId);
      }
    },
    [isMobile, selectServiceById]
  );

  const handleSelectService = useCallback(() => {
    if (activeItem) {
      selectServiceById(activeItem);
    }
  }, [activeItem, selectServiceById]);

  const handleOpenAllService = useCallback(() => {
    setOpenPopupAllService(true);
    if (pathsLength >= 2) {
      setIsEstimateAllService(true);
    }
  }, [pathsLength, setOpenPopupAllService, setIsEstimateAllService]);

  const handleClosePopupAllService = useCallback(() => {
    setOpenPopupAllService(false);
    setIsEstimateAllService(false);
  }, [setOpenPopupAllService, setIsEstimateAllService]);

  // ============================================================================
  // Sync Active Item with Selected Service
  // ============================================================================

  useEffect(() => {
    if (selectedService?._id && isOpenPopupAllService) {
      setActiveItem(selectedService._id);
    }
  }, [selectedService?._id, isOpenPopupAllService]);

  // ============================================================================
  // Render Function
  // ============================================================================

  const renderHmEstimatedServices = useCallback(
    () =>
      hmEstimatedServicesGroup ? (
        <HmServiceList
          hmEstimatedServicesGroup={hmEstimatedServicesGroup}
          activeItem={activeItem}
          localeSuffix={localeSuffix}
          isMobile={isMobile ?? false}
          hasServiceAvailable={false}
          isEstimatingIntercityOrder={isEstimatingIntercityOrder}
          isEstimatingNewOrder={isEstimatingNewOrder}
          surgePriceConfig={surgePriceConfig}
          onSelectVehicle={handleSelectVehicle}
          onSelectService={handleSelectService}
        />
      ) : null,
    [
      hmEstimatedServicesGroup,
      activeItem,
      localeSuffix,
      isMobile,
      isEstimatingIntercityOrder,
      isEstimatingNewOrder,
      surgePriceConfig,
      handleSelectVehicle,
      handleSelectService,
    ]
  );

  // ============================================================================
  // Return Value
  // ============================================================================

  return {
    isDisabled: isLoadingServiceTypes || !pickupPath,
    isLoading: isLoadingServiceTypes || loadingLocation,
    isHiddenHm: !isHMService || !estimatedServices.length || pathsLength !== 2,
    estimatedServices,
    hmEstimatedServicesGroup,
    pickupPath,
    onOpenPopupAllService: handleOpenAllService,
    onClosePopupAllService: handleClosePopupAllService,
    renderHmEstimatedServices,
  };
}
